/**
 * ModelResolver — resolves a model_class to a concrete adapter + model pair.
 *
 * Resolution priority (highest wins):
 *   1. AGENT_RUNTIME_MODEL_{FAST|STANDARD|THINKING} env var
 *   2. AGENT_RUNTIME_MODEL env var (catch-all fallback)
 *   3. agent-runtime.config.yaml model_mapping.<class>
 *   4. CLI --adapter + adapter default model
 */

export type ModelClass = "fast" | "standard" | "thinking";

export interface ResolvedModel {
  adapter: string;
  model?: string;
}

export interface ModelResolver {
  resolve(modelClass: ModelClass | undefined): ResolvedModel;
}

export interface ModelMappingConfig {
  fast?: { adapter: string; model?: string };
  standard?: { adapter: string; model?: string };
  thinking?: { adapter: string; model?: string };
}

export interface ModelResolverOptions {
  modelMapping?: ModelMappingConfig;
  fallbackAdapter: string;
  fallbackModel?: string;
  env?: Record<string, string | undefined>;
}

const ENV_PREFIX = "AGENT_RUNTIME_MODEL";
const CLASS_ENV_KEYS: Record<ModelClass, string> = {
  fast: `${ENV_PREFIX}_FAST`,
  standard: `${ENV_PREFIX}_STANDARD`,
  thinking: `${ENV_PREFIX}_THINKING`,
};

/**
 * Parse an "adapter:model" string.
 * - "claude:claude-sonnet-4-20250514" → { adapter: "claude", model: "claude-sonnet-4-20250514" }
 * - "claude:" → { adapter: "claude" }
 * - "claude-sonnet-4-20250514" (no colon) → { model: "claude-sonnet-4-20250514" } (adapter from fallback)
 */
function parseEnvValue(
  value: string,
  fallbackAdapter: string,
): ResolvedModel {
  const colonIdx = value.indexOf(":");
  if (colonIdx === -1) {
    return { adapter: fallbackAdapter, model: value || undefined };
  }
  const adapter = value.slice(0, colonIdx) || fallbackAdapter;
  const model = value.slice(colonIdx + 1) || undefined;
  return { adapter, model };
}

export function createModelResolver(
  options: ModelResolverOptions,
): ModelResolver {
  const env = options.env ?? process.env;

  return {
    resolve(modelClass: ModelClass | undefined): ResolvedModel {
      const fallback: ResolvedModel = {
        adapter: options.fallbackAdapter,
        model: options.fallbackModel,
      };

      if (!modelClass) return fallback;

      // 1. Class-specific env var
      const classEnv = env[CLASS_ENV_KEYS[modelClass]];
      if (classEnv) {
        return parseEnvValue(classEnv, options.fallbackAdapter);
      }

      // 2. Catch-all env var
      const catchAllEnv = env[ENV_PREFIX];
      if (catchAllEnv) {
        return parseEnvValue(catchAllEnv, options.fallbackAdapter);
      }

      // 3. Config file model_mapping
      const entry = options.modelMapping?.[modelClass];
      if (entry) {
        return { adapter: entry.adapter, model: entry.model };
      }

      // 4. Fallback
      return fallback;
    },
  };
}
